import {
    Box,
    Typography,
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import {
    Business as CompanyIcon,
    InsertDriveFile as FileIcon,
    CloudUpload as UploadIcon,
    LocalShipping as VehicleIcon,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import {
    useGetDocumentsByEntityQuery,
    useDeleteDocumentMutation,
    DocumentResponseDto,
    useReorderDocumentsMutation,
} from "../../../api/documentsApi";
import DocumentUploadModal from "../../../components/documents/DocumentUploadModal";
import { DataTable } from "../../../components/common/DataTable/DataTable";
import {
    createDocumentActions,
    createDocumentColumns,
    getDocumentRowSx,
    isDocumentExpired,
    isDocumentExpiringSoon,
} from "../../../components/documents/documentColumns";
import { useToast } from "../../../contexts/ToastContext";
import TabbedPageCard from "../../../components/common/TabbedPageCard";
import DocumentsTable from "../../documents/components/DocumentsTable";

interface VehicleDocumentsTabProps {
    vehicleId: number;
    showHeader?: boolean;
    showCompanyTab?: boolean;
}

const VehicleDocumentsTab: React.FC<VehicleDocumentsTabProps> = ({
    vehicleId,
    showHeader = true,
    showCompanyTab = true,
}) => {
    const [activeDocumentsTab, setActiveDocumentsTab] = useState(0);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState<DocumentResponseDto | null>(null);
    const [documentToEdit, setDocumentToEdit] = useState<DocumentResponseDto | null>(null);
    const [orderedDocuments, setOrderedDocuments] = useState<DocumentResponseDto[]>([]);
    const { showError, showLoading, showSuccess } = useToast();

    const {
        data: documents,
        isLoading,
        isFetching,
        error,
        refetch,
    } = useGetDocumentsByEntityQuery({
        entityType: "Vehicle",
        entityId: vehicleId,
    });

    const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation();
    const [reorderDocuments] = useReorderDocumentsMutation();
    const isDocumentsLoading = isLoading || isFetching;
    const tableDocuments =
        isDocumentsLoading || orderedDocuments.length > 0 || !documents
            ? orderedDocuments
            : documents;

    useEffect(() => {
        setOrderedDocuments([]);
        setDocumentToEdit(null);
        setDocumentToDelete(null);
    }, [vehicleId]);

    useEffect(() => {
        if (!isDocumentsLoading) {
            setOrderedDocuments(documents ?? []);
        }
    }, [documents, isDocumentsLoading]);

    const handleDownload = (document: DocumentResponseDto) => {
        // Open the document URL in a new tab for download
        window.open(document.url, '_blank');
    };

    const handleView = (document: DocumentResponseDto) => {
        // Open the document URL in a new tab for viewing
        window.open(document.url, '_blank');
    };

    const handleDeleteClick = (document: DocumentResponseDto) => {
        setDocumentToDelete(document);
        setDeleteDialogOpen(true);
    };

    const handleEditClick = (document: DocumentResponseDto) => {
        setDocumentToEdit(document);
        setEditModalOpen(true);
    };

    const handleReorder = async (orderedIds: (number | string)[]) => {
        const previousDocuments = tableDocuments;
        const nextDocuments = orderedIds
            .map((id) => previousDocuments.find((document) => document.id === Number(id)))
            .filter((document): document is DocumentResponseDto => Boolean(document));

        setOrderedDocuments(nextDocuments);
        showLoading("Saving document order...");

        try {
            await reorderDocuments({
                entityType: "Vehicle",
                entityId: vehicleId,
                orderedDocumentIds: orderedIds.map(Number),
            }).unwrap();
            showSuccess("Document order updated successfully.");
        } catch (error) {
            setOrderedDocuments(previousDocuments);
            showError("Unable to update document order. Please try again.");
            console.error("Failed to reorder documents:", error);
        }
    };

    const handleConfirmDelete = async () => {
        if (!documentToDelete) return;

        try {
            await deleteDocument(documentToDelete.id).unwrap();
            setDeleteDialogOpen(false);
            setDocumentToDelete(null);
            refetch(); // Refresh the documents list
        } catch (error) {
            console.error('Failed to delete document:', error);
            // Error handling will be shown by the mutation error state
        }
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setDocumentToDelete(null);
    };

    const documentColumns = createDocumentColumns();
    const documentActions = createDocumentActions({
        isDeleting,
        onDelete: handleDeleteClick,
        onDownload: handleDownload,
        onEdit: handleEditClick,
        onView: handleView,
    });

    const renderDocumentsTable = (loading = false) => (
        <DataTable
            items={loading ? [] : tableDocuments}
            columns={documentColumns}
            actions={documentActions}
            totalCount={loading ? 0 : tableDocuments.length}
            getRowSx={getDocumentRowSx}
            isLoading={loading}
            fitContentHeight
            enableRowReorder={!loading}
            getRowId={(document) => document.id}
            onReorder={handleReorder}
        />
    );

    const renderContent = () => {
        if (isDocumentsLoading) {
            return renderDocumentsTable(true);
        }

        if (error) {
            return (
                <Alert severity="error">
                    Error loading documents. Please try again later.
                </Alert>
            );
        }

        if (!documents || tableDocuments.length === 0) {
            return (
                <Box sx={{ p: 3, textAlign: "center" }}>
                    <FileIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Documents Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        There are no documents uploaded for this vehicle yet.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Click the "Upload Document" button above to add your first document.
                    </Typography>
                </Box>
            );
        }

        return (
            <>
                <Box sx={{ mb: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                        {tableDocuments.length} document{tableDocuments.length !== 1 ? "s" : ""} total
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            Active: {tableDocuments.filter(d => d.isActive).length}
                        </Typography>
                        <Typography variant="caption" color="primary">
                            Permits: {tableDocuments.filter(d => d.isPermit).length}
                        </Typography>
                        <Typography variant="caption" color="error">
                            Expired: {tableDocuments.filter(d => isDocumentExpired(d.expirationDate)).length}
                        </Typography>
                        <Typography variant="caption" color="warning.dark">
                            Expiring Soon: {tableDocuments.filter(d => isDocumentExpiringSoon(d.expirationDate) && !isDocumentExpired(d.expirationDate)).length}
                        </Typography>
                    </Box>
                </Box>

                {renderDocumentsTable()}
            </>
        );
    };

    const isCompanyTab = showCompanyTab && activeDocumentsTab === 1;

    return (
        <Box>
            {showHeader && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Vehicle Documents
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage and view documents related to this vehicle.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<UploadIcon />}
                        onClick={() => setUploadModalOpen(true)}
                        sx={{ textTransform: "none" }}
                    >
                        Upload Document
                    </Button>
                </Box>
            )}

            {showCompanyTab ? (
                <TabbedPageCard
                    idPrefix="vehicle-documents"
                    ariaLabel="vehicle document tabs"
                    variant="flat"
                    selectedTab={activeDocumentsTab}
                    onTabChange={setActiveDocumentsTab}
                    panelSx={{ pt: 2 }}
                    tabs={[
                        {
                            label: "Vehicle",
                            icon: <VehicleIcon />,
                            iconPosition: "start",
                            content: renderContent(),
                        },
                        {
                            label: "Company",
                            icon: <CompanyIcon />,
                            iconPosition: "start",
                            content: (
                                <DocumentsTable
                                    entityType="Company"
                                    entityLabel="Company"
                                    showEntityFilter={false}
                                    fitContentHeight
                                />
                            ),
                        },
                    ]}
                />
            ) : (
                renderContent()
            )}

            {showHeader && (
                <DocumentUploadModal
                    open={uploadModalOpen}
                    onClose={() => setUploadModalOpen(false)}
                    entityType={isCompanyTab ? "Company" : "Vehicle"}
                    entityId={isCompanyTab ? 0 : vehicleId}
                    entityLabel={isCompanyTab ? "Company" : "Vehicle"}
                    onSuccess={() => {
                        refetch();
                        setUploadModalOpen(false);
                    }}
                />
            )}

            <DocumentUploadModal
                open={editModalOpen}
                onClose={() => {
                    setEditModalOpen(false);
                    setDocumentToEdit(null);
                }}
                entityType="Vehicle"
                entityId={vehicleId}
                mode="replace"
                document={documentToEdit}
                onSuccess={() => {
                    refetch();
                    setEditModalOpen(false);
                    setDocumentToEdit(null);
                }}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCancelDelete}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete Document
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete "{documentToDelete?.originalFileName}"?
                        This action cannot be undone and will permanently remove the document from the system.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={isDeleting}
                    >
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VehicleDocumentsTab; 